// Dinero siempre en centavos enteros. Nunca usar números flotantes para montos.

/** Formatea centavos como moneda. 123456 -> "$1,234.56" */
export function formatCents(cents: number, currency = 'USD', locale = 'es-US'): string {
  if (!Number.isInteger(cents)) throw new Error(`Monto inválido (no entero): ${cents}`);
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

/**
 * Convierte texto escrito por una persona a centavos, sin aritmética flotante.
 * Acepta "1234", "1,234.5", "$1,234.56", " 12.3 ". Devuelve null si no es válido.
 * No acepta negativos ni más de 2 decimales.
 */
export function parseMoneyToCents(input: string): number | null {
  if (typeof input !== 'string') return null;
  const clean = input.trim().replace(/^\$/, '').replace(/,/g, '').trim();
  if (!/^\d+(\.\d{0,2})?$/.test(clean)) return null;
  const [whole, frac = ''] = clean.split('.');
  const cents = Number(whole) * 100 + Number((frac + '00').slice(0, 2));
  if (!Number.isSafeInteger(cents)) return null;
  return cents;
}

export function sumCents(values: readonly number[]): number {
  let total = 0;
  for (const v of values) {
    if (!Number.isInteger(v)) throw new Error(`Monto inválido (no entero): ${v}`);
    total += v;
  }
  return total;
}

/** Centavos a texto de entrada editable: 123450 -> "1234.50" */
export function centsToInput(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

// ---- Comisiones del procesador (estimación para "cubrir la comisión") ----
// Tarifas estándar de Stripe en EE. UU.; si cambian, se ajustan aquí.
export const PROCESSOR_FEES = {
  card: { percentBp: 290, fixedCents: 30, capCents: null as number | null },
  ach: { percentBp: 80, fixedCents: 0, capCents: 500 as number | null },
} as const;

/** Comisión estimada sobre un monto bruto (basis points: 290 = 2.90%). */
export function processorFee(grossCents: number, method: 'card' | 'ach'): number {
  const f = PROCESSOR_FEES[method];
  const pct = Math.round((grossCents * f.percentBp) / 10_000);
  const fee = pct + f.fixedCents;
  return f.capCents !== null ? Math.min(fee, f.capCents) : fee;
}

/** Monto a cobrar para que a la iglesia le lleguen `netCents` después de la comisión. */
export function grossUpForFees(netCents: number, method: 'card' | 'ach'): number {
  if (!Number.isInteger(netCents) || netCents <= 0) throw new Error('Monto inválido');
  const f = PROCESSOR_FEES[method];
  let gross = Math.ceil((netCents + f.fixedCents) / (1 - f.percentBp / 10_000));
  if (f.capCents !== null) gross = Math.min(gross, netCents + f.capCents);
  while (gross - processorFee(gross, method) < netCents) gross++;
  // El menor monto que cubre la comisión (no cobrar de más).
  while (gross - 1 - processorFee(gross - 1, method) >= netCents) gross--;
  return gross;
}
